'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useWalletIsConnected from '../../../hooks/useWalletIsConnected';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import { useQueryClient } from '@tanstack/react-query';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import React from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import { SHORT_TERM_API_PAGE_SIZE } from '../../../constants/historyTables';
import { OrderHistoryFilters } from './OrderHistoryFilters';
import { useShortTermHistoryQuery } from 'src/hooks/historyData/useShortTermHistoryQuery';
import { useHistoryPagination } from 'src/hooks/historyData/useHistoryPagination';
import { Serializer } from '@velocity-exchange/common';
import type { ParentOrderInfo } from 'src/@types/historyTables';
import {
	convertV2OrderRecordToParentOrderInfo,
	orderHistoryFilter,
} from 'src/utils/historyTables';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import { cm } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';
import {
	getOrderHistoryColumns,
	OrderHistoryTableMeta,
} from './orderHistoryColumns';
import { OrderRowCollapsedContentDesktop } from './OrderRowCollapsedContent';
import { TradePageOrderHistoryMobileCard } from './TradePageOrderHistoryMobileCard';
import { ExpandableRowDataTable } from 'src/components/primitives/Table/ExpandableRowDataTable';
import ContentGuard from 'src/components/ContentGuard';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';

const PAGE_SIZE = SHORT_TERM_API_PAGE_SIZE;
const EMPTY_MESSAGE = 'No orders found';

interface Props extends WithClassName {
	emptyClassName?: string;
	filtersWrapperClassName?: string;
	tableContentWrapperClassName?: string;
}

export type AmountDisplayType = 'base' | 'quote';

const TradePageOrderHistoryTable = ({
	className,
	emptyClassName,
	filtersWrapperClassName,
	tableContentWrapperClassName,
}: Props) => {
	const isMobile = useIsMobileScreenSize();
	const filters = useHistoryTableFilterStore((s) => s.getFilterState('orders'));
	const currentPage = filters.currentUiPage;

	const [amountDisplayType, setAmountDisplayType] =
		useState<AmountDisplayType>('base');
	const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();

	const handleToggleAmountDisplayType = useCallback(() => {
		setAmountDisplayType((prev) => (prev === 'base' ? 'quote' : 'base'));
	}, []);

	const handleToggleExpand = useCallback((orderId: number) => {
		setExpandedRows((prev) => ({
			...prev,
			[orderId]: !prev[orderId],
		}));
	}, []);

	const currentAccountPubKey = useDriftAccountsStore(
		(s) => s.getCurrentUserAccount()?.pubKey
	);

	const deserializer = useCallback(
		(r: Record<string, unknown>): ParentOrderInfo =>
			convertV2OrderRecordToParentOrderInfo(
				// @ts-expect-error
				Serializer.Deserialize.UIOrderRecordV2(r)
			),
		[]
	);

	const selectedMarketType = filters.marketType;

	const queryClient = useQueryClient();
	const appEventEmitter = useAppEventEmitter();
	const currentAuthority = useCurrentAuthority();

	const {
		allRecords: orderRecords,
		isLoading: loading,
		isFetching,
		hasNextPage,
		fetchNextPage,
		totalPagesFetched,
	} = useShortTermHistoryQuery<ParentOrderInfo>('orders', deserializer, true, {
		marketFilter: selectedMarketType,
		hasFill: filters.showOnlyFilledOrders || undefined,
	});

	const { currentApiPage, executePageChange } = useHistoryPagination('orders');

	// Refetch orders when a trade fill is received so fill status updates
	const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		const refetchAfterDelay = () => {
			if (refetchTimeoutRef.current) {
				clearTimeout(refetchTimeoutRef.current);
			}
			refetchTimeoutRef.current = setTimeout(() => {
				queryClient.invalidateQueries({
					queryKey: createQueryKey(
						'short-term-history',
						'orders',
						currentAccountPubKey?.toString() ?? '',
						currentAuthority?.toString() ?? ''
					),
				});
				refetchTimeoutRef.current = null;
			}, 1000);
		};

		appEventEmitter.on('orderTradeRecordReceived', refetchAfterDelay);
		return () => {
			appEventEmitter.off('orderTradeRecordReceived', refetchAfterDelay);
			if (refetchTimeoutRef.current) {
				clearTimeout(refetchTimeoutRef.current);
			}
		};
	}, [appEventEmitter, queryClient, currentAccountPubKey, currentAuthority]);

	// Fetch next page when needed
	useEffect(() => {
		if (currentApiPage >= totalPagesFetched && hasNextPage) {
			fetchNextPage();
		}
	}, [currentApiPage, totalPagesFetched, hasNextPage, fetchNextPage]);

	const isConnected = useWalletIsConnected();

	const processedOrderHistory = useMemo(() => {
		return (orderRecords ?? [])
			.filter((record) => orderHistoryFilter(record, filters))
			.sort((a, b) => b.lastUpdatedTs.toNumber() - a.lastUpdatedTs.toNumber());
	}, [orderRecords, filters]);

	const currentPageOrderHistory = useMemo(() => {
		return processedOrderHistory.slice(
			currentPage * PAGE_SIZE,
			(currentPage + 1) * PAGE_SIZE
		);
	}, [processedOrderHistory, currentPage]);

	useEffect(() => {
		executePageChange(0, PAGE_SIZE);
	}, [
		currentAccountPubKey?.toString(),
		selectedMarketType,
		filters.marketSymbol,
		filters.showOnlyFilledOrders,
	]);

	const columns = useMemo(
		() =>
			getOrderHistoryColumns({
				onToggleDateDisplay: toggleDateDisplayType,
				onToggleAmountDisplay: handleToggleAmountDisplayType,
				amountDisplayType,
			}),
		[toggleDateDisplayType, handleToggleAmountDisplayType, amountDisplayType]
	);

	const tableMeta: OrderHistoryTableMeta = {
		expandedRows,
		onToggleExpand: handleToggleExpand,
		amountDisplayType,
		displayDatesAsTimestamp,
	};

	const isLoading = loading || (isFetching && !currentPageOrderHistory.length);

	const mobileContent = (
		<div
			className={cm(
				MOBILE_TABLE_CARD_LIST_CLASSNAME,
				tableContentWrapperClassName
			)}
		>
			{currentPageOrderHistory.length > 0 ? (
				currentPageOrderHistory.map((orderInfo) => {
					const orderedQuoteValue = orderInfo.price.eqZero()
						? null
						: orderInfo.baseAssetAmount
								.mul(orderInfo.price)
								.shiftTo(orderInfo.price.precision)
								.toNotional();

					return (
						<TradePageOrderHistoryMobileCard
							key={orderInfo.orderId}
							orderInfo={orderInfo}
							displayDatesAsTimestamp={displayDatesAsTimestamp}
							toggleDateDisplayType={toggleDateDisplayType}
							amountDisplayType={amountDisplayType}
							onToggleAmountDisplayType={handleToggleAmountDisplayType}
							orderedQuoteValue={orderedQuoteValue}
						/>
					);
				})
			) : (
				<MobileTableCard.EmptyPlaceholder>
					{EMPTY_MESSAGE}
				</MobileTableCard.EmptyPlaceholder>
			)}
		</div>
	);

	const handlePageChange = useCallback(
		(pageIndex: number) => {
			executePageChange(pageIndex, PAGE_SIZE);
		},
		[executePageChange]
	);

	const maxCount = hasNextPage
		? Math.max(processedOrderHistory.length + 1, PAGE_SIZE + 1)
		: processedOrderHistory.length;

	const handleRowClick = useCallback(
		(row: ParentOrderInfo) => {
			handleToggleExpand(row.orderId);
		},
		[handleToggleExpand]
	);

	const renderExpandedRow = useCallback(
		(orderInfo: ParentOrderInfo) => {
			const orderedQuoteValue = orderInfo.price.eqZero()
				? null
				: orderInfo.baseAssetAmount
						.mul(orderInfo.price)
						.shiftTo(orderInfo.price.precision)
						.toNotional();

			return (
				<div className="bg-main-bg px-lg pb-md">
					<OrderRowCollapsedContentDesktop
						orderId={orderInfo.orderId}
						displayDatesAsTimestamp={displayDatesAsTimestamp}
						expanded
						amountDisplayType={amountDisplayType}
						orderedQuoteValue={orderedQuoteValue}
					/>
				</div>
			);
		},
		[displayDatesAsTimestamp, amountDisplayType]
	);

	const desktopContent = (
		<ExpandableRowDataTable
			columns={columns}
			data={currentPageOrderHistory}
			isRowExpanded={(row) => expandedRows[row.orderId] ?? false}
			onRowClick={handleRowClick}
			renderExpandedRow={renderExpandedRow}
			emptyMessage={EMPTY_MESSAGE}
			emptyClassName={emptyClassName}
			meta={tableMeta}
			columnPinning={{ right: ['expand'] }}
			pagination={{
				pageIndex: currentPage,
				pageSize: PAGE_SIZE,
				totalCount: maxCount,
				onPageChange: handlePageChange,
			}}
		/>
	);

	return (
		<div className={cm('flex h-full flex-col', className)}>
			<div
				className={cm(
					'flex-shrink-0 border-stroke-divider p-md sm:border-b',
					filtersWrapperClassName
				)}
			>
				<OrderHistoryFilters />
			</div>
			<div className={cm('min-h-0 flex-1', tableContentWrapperClassName)}>
				<ContentGuard requireAccount requireWallet isLoading={isLoading}>
					{isConnected && (isMobile ? mobileContent : desktopContent)}
				</ContentGuard>
			</div>
		</div>
	);
};

export default React.memo(TradePageOrderHistoryTable);
